<template>

    <Toast/>

    <MenuFinancialCompact v-if="userStore.user?.roles?.includes('ROLE_ISORISE')" />
    <MenuHealthCompact v-else />

    <div class="px-6 surface-overlay relative">

        <ul class="list-none py-2 px-0 m-0 flex align-items-center font-medium overflow-x-auto w-full">
            <li class="pr-3"><a class="cursor-pointer">Financial Clarity</a></li>
            <li class="px-2"><i class="pi pi-angle-right text-900"></i></li>
            <li class="px-2"><a class="cursor-pointer text-900 white-space-nowrap">Billing Runs</a></li>
        </ul>

        <div v-show="activeTab === 1" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Manage Billing Runs</h3>

                    <div class="grid">
                        <ul class="list-none p-0 m-0 flex lg:align-items-center flex-column lg:flex-row select-none">
                            <li>
                                <a @click="callNewBillingRun" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>New Billing Run</span>
                                </a>
                            </li>
                        </ul>
                    </div>

                    <DataTable
                        :value="billingRuns"
                        :paginator="true"
                        class="p-datatable-gridlines"
                        :rows="100"
                        dataKey="id"
                        :rowsPerPageOptions="[10,20,50,100]"
                        :rowHover="true"
                        :striped-rows="true"
                        :reorderableColumns="true"
                        :sortMode="'multiple'"
                        v-model:filters="filters"
                        filterDisplay="menu"
                        :loading="loading"
                        responsiveLayout="scroll"
                        :globalFilterFields="['id', 'startDate', 'endDate', 'productName']">

                        <template #header>
                            <div class="p-grid">
                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <MultiSelect :modelValue="selectedColumns" :options="columnsFullList"
                                                     optionLabel="header"
                                                     @update:modelValue="onToggleColumns"
                                                     display="chip" placeholder="Select Columns"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <MultiSelect :modelValue="selectedStandardFilter" :options="standardFilters"
                                                     optionLabel="header"
                                                     @update:modelValue="onToggleStandardFilters"
                                                     display="chip" placeholder="Select Standard Filter"/>
                                    </div>
                                </div>

                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-filter-slash" label="Clear Search"
                                                class="p-button-outlined p-ml-2" @click="clearFilter()"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-replay" label="Restore to Original View"
                                                class="p-button-outlined" @click="restoreToOriginal()"/>
                                    </div>
                                </div>

                                <div class="p-col-12 p-md-2">
                                    <span class="p-input-icon-left">
                                        <i class="pi pi-search"/>
                                        <InputText v-model="filters['global'].value"
                                                   placeholder="Keyword Search on ID, Start Date, End Date or Product Name"
                                                   style="width: 100%"/>
                                    </span>
                                </div>
                            </div>

                        </template>

                        <template #empty>
                            No billing runs found.
                        </template>

                        <template #loading>
                            Loading billing runs. Please wait.
                        </template>

                        <Column field="selectAnOption" style="width: 8%">
                            <template #body="{ data }">
                                <Button v-tooltip="'View'" icon="pi pi-eye"
                                        class="p-button-rounded p-button-help m-1"
                                        @click="callViewInvoices(data)"/>

                                <Button v-tooltip="'Delete'" icon="pi pi-trash"
                                        class="p-button-rounded p-button-danger m-1"
                                        @click="callDeleteBillingRun(data)"/>

                            </template>
                        </Column>

                        <Column field="id" header="ID" :sortable="true"/>

                        <template v-for="col in selectedColumns" :key="col.field">

                            <Column v-if="col.field === 'type'" :field="col.field" :header="col.header"
                                    :sortable="col.sortable">
                                <template #body="{data}">
                                    <span class="p-column-title">Type</span>
                                    <span v-if="data.type === 'A'"
                                          :class="'type-badge status-' + data.type">Appointments</span>
                                    <span v-if="data.type === 'P'"
                                          :class="'type-badge status-' + data.type">Products</span>
                                </template>
                            </Column>

                            <Column v-else-if="col.field === 'status'" :field="col.field" :header="col.header"
                                    :sortable="col.sortable">
                                <template #body="{data}">
                                    <span class="p-column-title">Status</span>
                                    <span v-if="data.status === 'I'"
                                          :class="'status-badge status-' + data.status">Initiated</span>
                                    <span v-if="data.status === 'P'"
                                          :class="'status-badge status-' + data.status">Paid</span>
                                    <span v-if="data.status === 'C'"
                                          :class="'status-badge status-' + data.status">Completed</span>
                                </template>
                            </Column>

                            <Column v-else :field="col.field" :header="col.header" :sortable="col.sortable"/>

                        </template>


                    </DataTable>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 2" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>View Invoices for Billing Run from {{ this.billingRun.startDate }} to {{
                            this.billingRun.endDate
                        }}</h3>

                    <DataTable
                        :value="invoices"
                        :paginator="true"
                        class="p-datatable-gridlines"
                        :rows="100"
                        dataKey="id"
                        :rowsPerPageOptions="[10,20,50,100]"
                        :rowHover="true"
                        :striped-rows="true"
                        :reorderableColumns="true"
                        :sortMode="'multiple'"
                        responsiveLayout="scroll">

                        <template #empty>
                            No invoices found.
                        </template>

                        <template #loading>
                            Loading invoices. Please wait.
                        </template>

                        <Column field="selectAnOption" style="width: 5%">
                            <template #body="{ data }">
                                <Button v-tooltip="'View'" icon="pi pi-eye"
                                        class="p-button-rounded p-button-help m-1"
                                        @click="callViewInvoice(data)"/>
                            </template>
                        </Column>

                        <Column field="status" header="Status" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Status</span>
                                <span v-if="data.status === 'I'"
                                      :class="'status-badge status-' + data.status">Unpaid</span>
                                <span v-if="data.status === 'S'"
                                      :class="'status-badge status-' + data.status">Submitted</span>
                                <span v-if="data.status === 'P'"
                                      :class="'status-badge status-' + data.status">Paid</span>
                                <span v-if="data.status === 'U'"
                                      :class="'status-badge status-' + data.status">Unpaids Process</span>

                            </template>
                        </Column>

                        <Column field="id" header="Invoice #" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Invoice #</span>
                                {{ data.uniqueId }}
                            </template>
                        </Column>

                        <Column field="invoiced" header="Invoiced" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Invoiced</span>
                                {{ data.invoiced }}
                            </template>
                        </Column>

                        <Column field="appointmentDate" header="Appointment Date" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Appointment Date</span>
                                {{ data.appointmentDate }}
                            </template>
                        </Column>

                        <Column field="amount" header="Amount" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Amount</span>
                                {{ format_amount(data.amount) }}
                            </template>
                        </Column>

                        <Column field="practitionerName" header="Practitioner Name" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Practitioner Name</span>
                                {{ data.practitionerName }}
                            </template>
                        </Column>

                        <Column field="clientName" header="Client Name" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Client Name</span>
                                {{ data.clientName }}
                            </template>
                        </Column>

                        <Column field="billingPlanName" header="Billing Plan" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Billing Plan</span>
                                {{ data.billingPlanName }}
                            </template>
                        </Column>

                    </DataTable>

                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>


        <div v-show="activeTab === 3" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Delete Billing Run from {{ this.billingRun.startDate }} to {{ this.billingRun.endDate }}</h3>

                    <div class="field">
                        <label for="deleteConfirmationResponse">Do You Wish to Delete this Billing Run?</label>
                        <Dropdown v-model="confirmationResponse" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <Button label="Delete" class="mr-2 mb-2" @click="deleteBillingRun"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>


        <div v-show="activeTab === 4" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Create Billing Run</h3>

                    <div class="field">
                        <label for="startDate">Start Date</label>
                        <Calendar id="startDate" v-model="newBillingRun.startDate" required="true"
                                  :showTime="true"
                                  :showSeconds="true" :showIcon="true" dateFormat="yy-mm-dd"/>

                    </div>

                    <div class="field">
                        <label for="endDate">End Date</label>
                        <Calendar id="endDate" v-model="newBillingRun.endDate" required="true"
                                  :showTime="true"
                                  :showSeconds="true" :showIcon="true" dateFormat="yy-mm-dd"/>

                    </div>

                    <div class="field">
                        <label for="type">Type</label>
                        <Dropdown v-model="newBillingRun.type" :options="billingRunTypes" optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select a Type"/>
                    </div>

                    <div class="field">
                        <label for="type">Invoice Generation Method</label>
                        <Dropdown v-model="newBillingRun.invoiceMethod" :options="invoiceMethodTypes"
                                  optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select an Option"/>
                    </div>

                    <div v-show="(companyConfiguration.type==='H') && (newBillingRun.invoiceMethod==='I')"
                         class="field">
                        <label for="submissionOption">Invoice Submission Method</label>
                        <Dropdown v-model="newBillingRun.healthInsuranceSubmission"
                                  :options="healthInsuranceSubmissionMethod"
                                  optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select an Option"/>
                    </div>

<!--                    <div v-show="(companyConfiguration.type==='H') && (newBillingRun.invoiceMethod==='I')"-->
                    <div v-show="(companyConfiguration.type==='H')"
                         class="field">
                        <label for="useSystemProcedureRates">Use System Procedure Rates?</label>
                        <Dropdown v-model="newBillingRun.useSystemProcedureRates" :options="confirmationTypes"
                                  optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select an Option"/>
                    </div>

                    <div v-show="newBillingRun.useSystemProcedureRates==='Y'" class="field">
                        <label for="rateOption">Rate Option?</label>
                        <Dropdown v-model="newBillingRun.rateOption" :options="rateOptionTypes"
                                  optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select an Option"/>
                    </div>

                    <Button label="Create" class="mr-2 mb-2" @click="createBillingRun"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>


    </div>

    <IsoEvolveFooter/>

</template>

<script>
import IsoEvolveFooter from "@/components/heading/authentication/IsoEvolveFooter.vue";
import MenuHealthCompact from "@/components/menu/health/MenuHealthCompact.vue";
import {useUserStore} from "@/stores/UserStore";
import UserService from "@/service/UserService";
import {FilterMatchMode, FilterOperator} from "primevue/api";
import CompanyService from "@/service/CompanyService";
import BillingRunService from "@/service/BillingRunService";
import moment from "moment/moment";
import InvoiceService from "@/service/InvoiceService";
import MenuFinancialCompact from "@/components/menu/famodule/MenuFinancialCompact.vue";

let numeral = require('numeral');

export default {
    components: {
        MenuFinancialCompact,
        IsoEvolveFooter, MenuHealthCompact
    },
    setup() {
        const userStore = useUserStore()

        return {
            userStore
        }
    },
    created() {
        this.initFilters();

        this.columnsDefault = [
            {field: 'type', header: 'Type', sortable: true},
            {field: 'status', header: 'Status', sortable: true},
            {field: 'startDate', header: 'Start Date', sortable: true},
            {field: 'endDate', header: 'End Date', sortable: true}
        ];

        this.columnsFullList = [
            {field: 'type', header: 'Type', sortable: true},
            {field: 'status', header: 'Status', sortable: true},
            {field: 'startDate', header: 'Start Date', sortable: true},
            {field: 'endDate', header: 'End Date', sortable: true},
            {field: 'productName', header: 'Product Name', sortable: true}
        ];

        this.selectedColumns = this.columnsDefault;

        this.standardFilters = [
            {field: 'appointments', header: 'Appointments'},
            {field: 'products', header: 'Products'},
            {field: 'paid', header: 'Paid'},
            {field: 'completed', header: 'Completed'}
        ];

    },
    async mounted() {
        await this.loadCompanyContext();
    },
    data() {

        return {
            profile: {},
            activeTab: 1,
            companyConfiguration: {},
            userConfiguration: {},
            filters: null,
            loading: true,
            submitted: false,
            // filteredItemsCache: new Map(),
            billingRun: {},
            newBillingRun: {},
            billingRuns: null,
            originalBillingRuns: null,
            invoices: null,
            confirmationResponse: 'N',
            selectedColumns: null,
            columnsDefault: null,
            columnsFullList: null,
            selectedStandardFilter: null,
            standardFilters: null,
            confirmationTypes: [
                {name: 'Yes', code: 'Y'},
                {name: 'No', code: 'N'}
            ],
            statusTypes: [
                {name: 'Active', code: 'A'},
                {name: 'Inactive', code: 'I'}
            ],
            billingRunTypes: [
                {name: 'Appointments', code: 'A'},
                {name: 'Product', code: 'P'}
            ],
            invoiceMethodTypes: [
                {name: 'Individual', code: 'I'},
                {name: 'Consolidated', code: 'C'}
            ],
            healthInsuranceSubmissionMethod: [
                {name: 'Email', code: 'E'},
                {name: 'Electronic Data Interchange (EDI)', code: 'D'}
            ],
            rateOptionTypes: [
                {name: 'Base Rate (Default)', code: 'D'},
                {name: 'Premier Rate A', code: 'A'},
                {name: 'Premier Rate B', code: 'B'}
            ]
        }
    },
    watch: {
        'userStore.activeCompanyId': {
            immediate: false,
            async handler(newCompanyId, oldCompanyId) {
                if (!newCompanyId || newCompanyId === oldCompanyId) {
                    return;
                }

                console.log(
                    '[Appointments][watch] Company changed:',
                    oldCompanyId,
                    '→',
                    newCompanyId
                );

                await this.loadCompanyContext();
            }
        }
    },
    methods: {
        async loadCompanyContext() {
            try {
                // Attempt to get user data
                const userData = await UserService.getUser(this.userStore.user.id);
                this.profile = userData.data;
            } catch (e) {
                console.error("[AppointmentsNew][mounted] Error fetching user data = ", e);
                this.userStore.clearUser();
                this.$router.push('login');
                return;
            }

            this.profile.companyId = this.userStore.activeCompanyId;

            try {
                const [userConfigData] = await Promise.all([
                    UserService.getUserConfig(this.userStore.user.id)
                ]);

                this.userConfiguration = userConfigData.data;
                console.log("[PhoneCalls][mounted] userConfigData.data = ", userConfigData.data);
            } catch (e) {
                console.error("[PhoneCalls][mounted] Could not find user config for user = ", this.userStore.user.id);
            }

            try {
                const [billingRunsData, companyConfigData] = await Promise.all([
                    BillingRunService.getCompanyBillingRuns(this.profile.companyId),
                    CompanyService.getCompanyConfiguration(this.profile.companyId)
                ]);

                this.billingRuns = billingRunsData.data;
                this.originalBillingRuns = billingRunsData.data;
                console.log("[PhoneCalls][mounted] billingRunsData.data = ", billingRunsData.data);

                this.companyConfiguration = companyConfigData.data;
                console.log("[PhoneCalls][mounted] companyConfigData.data = ", companyConfigData.data);

                this.loading = false;

            } catch (e) {
                console.error("[PhoneCalls][mounted] Error fetching additional data = ", e);
            }

            console.log("[PhoneCalls][mounted] this.billingRuns = ", this.billingRuns);
            console.log("[PhoneCalls][mounted] this.userConfiguration = ", this.userConfiguration);
            console.log("[PhoneCalls][mounted] this.companyConfiguration = ", this.companyConfiguration);
        },
        initFilters() {
            this.filters = {
                'global': {value: null, matchMode: FilterMatchMode.CONTAINS},
                'id': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'startDate': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'endDate': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'productName': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                }
            }
        },
        clearFilter() {
            this.initFilters();
        },
        restoreToOriginal() {
            window.location.reload();
        },
        callReset() {
            this.activeTab = 1;
        },
        callNewBillingRun() {
            this.newBillingRun = {
                type: 'A',
                startDate: moment().startOf('month').toDate(),
                endDate: moment().toDate(),
                status: 'I',
                invoiceMethod: 'C',
                healthInsuranceSubmission: 'D',
                useSystemProcedureRates: 'N',
                useSystemProductRates: 'N',
                rateOption: 'D'
            };

            this.activeTab = 4;
        },
        callHelp() {
            console.log('[ConfigurationBilling][callHelp] Start.');
        },
        callSupport() {
            console.log('[ConfigurationBilling][callSupport] Start.');
        },
        async callViewInvoices(data) {
            this.activeTab = 2;
            this.billingRun = data;

            try {
                const [resultsData] = await Promise.all([
                    InvoiceService.getCompanyInvoicesForBillingRun(this.profile.companyId, this.billingRun.id)
                ]);

                this.invoices = resultsData.data;
            } catch (e) {
                console.error("[PhoneCalls][mounted] Could not find invoices. Error = ", e);
            }

        },
        callViewInvoice(data) {
            console.log('[BillingRuns][callViewInvoice] data = ', data);
            this.$router.push({name: 'commonFinancialInvoices', query: {inputInvoiceId: data.id}});
        },
        callDeleteBillingRun(data) {
            this.activeTab = 3;
            this.confirmationResponse = 'N';
            this.billingRun = data;
        },
        onToggleColumns(value) {
            this.selectedColumns = this.columnsFullList.filter(col => value.includes(col));
        },
        onToggleStandardFilters(value) {

            if (value.length > 1) {
                // Keep the last selected option
                this.selectedStandardFilter = [value[value.length - 1]];
            } else {
                this.selectedStandardFilter = value;
            }

            const isAppointments = value.some(item => item.field === "appointments");
            const isProducts = value.some(item => item.field === "products");
            const isPaid = value.some(item => item.field === "paid");
            const isCompleted = value.some(item => item.field === "completed");

            if (this.billingRuns !== null) {

                if (isAppointments) {
                    this.billingRuns = this.originalBillingRuns.filter(billingRun => billingRun.type === 'A');
                } else if (isProducts) {
                    this.billingRuns = this.originalBillingRuns.filter(billingRun => billingRun.type === 'P');
                } else if (isPaid) {
                    this.billingRuns = this.originalBillingRuns.filter(billingRun => billingRun.status === 'P');
                } else if (isCompleted) {
                    this.billingRuns = this.originalBillingRuns.filter(billingRun => billingRun.status === 'C');
                } else {
                    this.billingRuns = this.originalBillingRuns;
                }

            } else {
                this.billingRuns = null;
            }
        },
        // getFilteredItems(data) {
        //     if (this.filteredItemsCache.has(data.id)) {
        //         return this.filteredItemsCache.get(data.id);
        //     }
        //
        //     const items = [];
        //
        //     items.push({
        //         label: 'View Invoices',
        //         class: 'mb-4 mr-4 ml-4 mt-4',
        //         command: () => {
        //             this.callViewInvoices(data);
        //         }
        //     });
        //
        //     items.push({
        //         label: 'Delete',
        //         class: 'mb-4 mr-4 ml-4 mt-4',
        //         command: () => {
        //             this.callDeleteBillingRun(data);
        //         }
        //     });
        //
        //     console.log('[PhoneCalls][getFilteredItems] items = ', items);
        //
        //     this.filteredItemsCache.set(data.id, items);
        //
        //     return items;
        // },
        // chooseBlankSelection(data) {
        //     console.log('[PhoneCalls][chooseBlankSelection] data = ', data);
        //     this.$toast.add({
        //         severity: 'error',
        //         summary: 'Error',
        //         detail: 'Please select a valid option.',
        //         life: 3000
        //     });
        // },
        format_amount(value) {
            if (value) {
                return numeral(String(value )).format('0,0.00');
            }
        },
        convertDateIntoUtcTimezone(inputDate) {
            console.log("[convertDateIntoUtcTimezone] inputDate :", inputDate);

            try {
                const moment = require("moment-timezone");

                let inputTimeZone = "Europe/London";

                if (this.companyConfiguration.region == "ZA") {
                    inputTimeZone = "Africa/Johannesburg";
                }

                const targetTimeZone = "Etc/UTC"; // Assuming the input date is in UTC

                console.log("[convertDateIntoUtcTimezone] inputTimeZone :", inputTimeZone);
                console.log("[convertDateIntoUtcTimezone] targetTimeZone :", targetTimeZone);

                // Parse the input date string and set the input time zone
                const parsedDate = moment.tz(inputDate, "YYYY-MM-DD HH:mm:ss", inputTimeZone);

                console.log("[convertDateIntoUtcTimezone] parsedDate :", parsedDate);

                // Convert the date to the target time zone
                const outputDate = parsedDate.clone().tz(targetTimeZone);

                console.log("[convertDateIntoUtcTimezone] Output Date :", outputDate.format("YYYY-MM-DD HH:mm:ss"));

                return outputDate.format("YYYY-MM-DD HH:mm:ss");

            } catch (error) {
                console.error("[convertDateIntoLocalTimezone] Could not convert date. Returning input date. Error :", error);

                return inputDate;
            }

        },
        convertDateIntoLocalTimezone(inputDate) {
            console.log("[convertDateIntoLocalTimezone] inputDate :", inputDate);

            try {
                const moment = require("moment-timezone");
                const inputTimeZone = "Etc/UTC"; // Assuming the input date is in UTC

                let targetTimeZone = "Europe/London";

                if (this.companyConfiguration.region == "ZA") {
                    targetTimeZone = "Africa/Johannesburg";
                }

                console.log("[convertDateIntoLocalTimezone] targetTimeZone :", targetTimeZone);

                // Parse the input date string and set the input time zone
                const parsedDate = moment.tz(inputDate, "YYYY-MM-DD HH:mm:ss", inputTimeZone);

                console.log("[convertDateIntoLocalTimezone] parsedDate :", parsedDate);

                // Convert the date to the target time zone
                const outputDate = parsedDate.clone().tz(targetTimeZone);

                console.log("[convertDateIntoLocalTimezone] Output Date :", outputDate.format("YYYY-MM-DD HH:mm:ss"));

                return outputDate.format("YYYY-MM-DD HH:mm:ss");

            } catch (error) {
                console.error("[convertDateIntoLocalTimezone] Could not convert date. Returning input date. Error :", error);

                return inputDate;
            }

        },
        async createBillingRun() {

            this.submitted = true;

            if (this.newBillingRun.type) {

                try {
                    const startDateInput = new Date(this.convertDateIntoUtcTimezone(this.newBillingRun.startDate));
                    const yearStart = startDateInput.getFullYear();
                    const monthStart = String(startDateInput.getMonth() + 1).padStart(2, '0');
                    const dayStart = String(startDateInput.getDate()).padStart(2, '0');
                    const hoursStart = String(startDateInput.getHours()).padStart(2, '0');
                    const minutesStart = String(startDateInput.getMinutes()).padStart(2, '0');
                    const secondsStart = String(startDateInput.getSeconds()).padStart(2, '0');

                    const startDate = `${yearStart}-${monthStart}-${dayStart} ${hoursStart}:${minutesStart}:${secondsStart}`;

                    const endDateInput = new Date(this.convertDateIntoUtcTimezone(this.newBillingRun.endDate));
                    const yearEnd = endDateInput.getFullYear();
                    const monthEnd = String(endDateInput.getMonth() + 1).padStart(2, '0');
                    const dayEnd = String(endDateInput.getDate()).padStart(2, '0');
                    const hoursEnd = String(endDateInput.getHours()).padStart(2, '0');
                    const minutesEnd = String(endDateInput.getMinutes()).padStart(2, '0');
                    const secondsEnd = String(endDateInput.getSeconds()).padStart(2, '0');

                    const endDate = `${yearEnd}-${monthEnd}-${dayEnd} ${hoursEnd}:${minutesEnd}:${secondsEnd}`;

                    console.log("[" + name + "][saveBillingRun] this.newBillingRun.startDate = ", this.newBillingRun.startDate);
                    console.log("[" + name + "][saveBillingRun] startDate = ", startDate);

                    console.log("[" + name + "][saveBillingRun] this.newBillingRun.endDate = ", this.newBillingRun.endDate);
                    console.log("[" + name + "][saveBillingRun] endDate = ", endDate);

                    var newBillingRunDto = {
                        type: this.newBillingRun.type,
                        startDate: startDate,
                        endDate: endDate,
                        status: 'I',
                        submissionMethod: 'C',
                        invoiceMethod: this.newBillingRun.invoiceMethod,
                        healthInsuranceSubmission: this.newBillingRun.healthInsuranceSubmission,
                        useSystemProcedureRates: this.newBillingRun.useSystemProcedureRates,
                        useSystemProductRates: 'N',
                        rateOption: this.newBillingRun.rateOption
                    };

                    console.log("[" + name + "][saveBillingRun] newBillingRunDto = ", newBillingRunDto);

                    const response = await BillingRunService.createBillingRun(this.profile.companyId, newBillingRunDto);
                    console.log('[ClientsNew][saveNewClient] Response = ', response);

                    const [resultsData] = await Promise.all([
                        BillingRunService.getCompanyBillingRuns(this.profile.companyId)
                    ]);

                    this.billingRuns = resultsData.data;
                    this.originalBillingRuns = resultsData.data;

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Created billing run',
                        life: 3000
                    });

                } catch (error) {
                    console.error('[ClientsNew][saveNewClient] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to create billing run. Please try again.',
                        life: 3000
                    });
                }
            }

            this.newBillingRun = {};
            this.activeTab = 1;
            this.submitted = false;

        },
        async deleteBillingRun() {
            this.submitted = true;

            if (this.confirmationResponse.trim() === "Y") {

                if (this.billingRun.id) {

                    try {

                        const response = await BillingRunService.deleteBillingRun(this.billingRun.id);
                        console.log('[ClientsNew][saveNewClient] Response = ', response);

                        const [resultsData] = await Promise.all([
                            BillingRunService.getCompanyBillingRuns(this.profile.companyId)
                        ]);

                        this.billingRuns = resultsData.data;
                        this.originalBillingRuns = resultsData.data;

                        this.$toast.add({
                            severity: 'success',
                            summary: 'Successful',
                            detail: 'Deleted billing run',
                            life: 3000
                        });

                    } catch (error) {
                        console.error('[ClientsNew][saveNewClient] Error:', error);

                        this.$toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to delete billing run. Please try again.',
                            life: 3000
                        });
                    }
                }

            }

            this.activeTab = 1;
            this.submitted = false;
        }
    },
};
</script>

<style scoped lang="scss">
a {
    text-decoration: none;
}

::v-deep(.p-datatable-frozen-tbody) {
    font-weight: bold;
}

::v-deep(.p-datatable-scrollable .p-frozen-column) {
    font-weight: bold;
}

::v-deep(.p-progressbar) {
    height: .5rem;
    background-color: #D8DADC;

    .p-progressbar-value {
        background-color: #607D8B;
    }
}

.status-badge, .type-badge {
    border-radius: var(--border-radius);
    padding: .25em .5rem;
    text-transform: uppercase;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: .3px;
}

.status-badge {
    &.status-I {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-P {
        background: #FFCDD2;
        color: #C63737;
    }

    &.status-U {
        background: #FEEDAF;
        color: #8A5340;
    }

    &.status-S {
        background: #B3E5FC;
        color: #23547B;
    }

    &.status-F {
        background: #ECCFFF;
        color: #694382;
    }

    &.status-M {
        background: #FFD8B2;
        color: #805B36;
    }

    &.status-H {
        background: #ECCFFF;
        color: #694382;
    }

}

.type-badge {
    &.status-I {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-P {
        background: #FFCDD2;
        color: #C63737;
    }

    &.status-A {
        background: #FEEDAF;
        color: #8A5340;
    }

    &.status-C {
        background: #B3E5FC;
        color: #23547B;
    }

}

.tabview-custom {
    i, span {
        vertical-align: middle;
    }

    span {
        margin: 0 .5rem;
    }
}

.p-tabview p {
    line-height: 1.5;
    margin: 0;
}

.accordion-custom {
    i, span {
        vertical-align: middle;
    }

    span {
        margin: 0 .5rem;
    }
}

.p-accordion p {
    line-height: 1.5;
    margin: 0;
}

.mb-2 {
    margin-bottom: 1rem;
}

.mr-2 {
    margin-right: 1rem;
}

</style>
